import type {
  AppConfig,
  Block,
  CalendarColor,
  CalendarFeed,
  DateFormat,
  DecodedLocalFeed,
  DecodedShare,
  FeedCategory,
  FindReplaceRule,
  LocalLaneForShare,
  Locale,
  MatchPosition,
  NativeShareResult,
  Palette,
  ParsedEvent,
  Scheme,
  ShareImportDecision,
  SharedView_t,
  StyleVariant,
  Zoom,
} from './types';
import { BLOCK_OPTIONS, CALENDAR_COLORS, FEED_CATEGORIES, isShareableMode, MATCH_POSITIONS, PALETTES, SCHEMES, SCRATCHPAD_FEED_ID } from './types';
import { feedIdFor } from './ics';
import { loadScratchpad, makeScratchpadEvent } from './scratchpad';
import { isValidTimezone, parseRRule } from './recurrence';
import { safeHref } from './event-display';
import { isPinHash } from './kiosk-pin';
import { fromBase64Url, toBase64Url } from './base64url';
import { fragmentParams, updateLocation } from './url';

export const SHARE_URL_LIMIT = 2000;
export const SHARE_PARAM = 's';

// Payloads are deflate-compressed and carry this prefix ('.' never occurs in
// base64url, so the format is self-identifying). Compression buys roughly
// 2-3× more feeds/rules within SHARE_URL_LIMIT. Pre-compression links (plain
// base64url) are deliberately not decoded — they fail closed: no import
// prompt, param stripped.
const SHARE_FORMAT_PREFIX = '2.';

type SharedFeed = {
  // u is the feed URL with a leading `https://` stripped (remote feeds are assumed
  // https) — decode re-adds it. Other schemes (http://, webcal://) travel intact.
  u: string; n: string; h: 0 | 1; c?: FeedCategory; tz?: string;
  cl?: CalendarColor; bl?: Block; st?: StyleVariant;
  // sl: 'l' = sub-lanes by location.
  sl?: 'l';
  // Legacy: pre-merge links carried a separate travel tag; decoded back into `c`.
  tr?: 'international' | 'local';
};
type SharedRule = {
  // r absent = matte (match + decorate only); '' = replace with blank.
  i: string; f: string; r?: string; s: StyleVariant;
  c?: FeedCategory; cl?: CalendarColor; bl?: Block; p?: MatchPosition; x?: 1;
};
// A local (scratchpad) lane and its events — there is no URL to re-fetch, so the
// event snapshot travels inline. To keep links short, timestamps are stored coarse:
//   s = start in MINUTES, delta from the previous event's start in this lane
//       (the first event's s is absolute minutes);
//   e = duration in MINUTES (end - start).
// descriptionSnippet and uid are dropped (recomputed / regenerated on decode).
// A repeating event adds r = its RRULE, z = the zone it repeats in, and
// x = its skipped occurrences as minute offsets from its start.
type SharedLocalEvent = {
  t: string; s: number; e: number; a: 0 | 1;
  d?: string; l?: string; w?: string; c?: FeedCategory;
  r?: string; z?: string; x?: number[];
  // Legacy travel tag; decoded back into `c`.
  tr?: 'international' | 'local';
};
// h = hidden/disabled; df = this lane is the built-in Draft (scratchpad:default),
// so the recipient merges it into their own Draft rather than making a new lane.
type SharedLocalFeed = {
  n: string; c?: FeedCategory; tz?: string; h?: 0 | 1; df?: 1; ev: SharedLocalEvent[];
  // sl: 'l' = sub-lanes by location (as on SharedFeed).
  sl?: 'l';
  // Legacy travel tag; decoded back into `c`.
  tr?: 'international' | 'local';
};
type SharedView = { z?: Zoom; l?: Locale; d?: DateFormat; t?: Scheme; p?: Palette };
// kh = the kiosk PIN's hash (kiosk-pin.ts), never the PIN.
type SharedPayload = { f: SharedFeed[]; r: SharedRule[]; lf?: SharedLocalFeed[]; v?: SharedView; kh?: string };

const STYLE_VARIANTS: StyleVariant[] = [
  'none', 'outline', 'bold', 'inverted', 'dashed', 'muted', 'striked', 'hidden',
];
const ZOOMS: Zoom[] = ['month', 'quarter', 'half-year', 'year', '2-year'];
const LOCALES: Locale[] = ['en', 'el'];
const DATE_FORMATS: DateFormat[] = ['YYYY-MM-DD', 'DD MMM YYYY', 'DD.MM.YYYY', 'MM/DD/YYYY'];

// lib.dom types (De)CompressionStream's writable side as BufferSource, which
// pipeThrough's invariant generics reject for a Uint8Array stream; the pair is
// byte-in/byte-out at runtime.
type BytePair = ReadableWritablePair<Uint8Array, Uint8Array>;

async function deflateRaw(bytes: Uint8Array<ArrayBuffer>): Promise<Uint8Array> {
  const stream = new Blob([bytes]).stream().pipeThrough(
    new CompressionStream('deflate-raw') as unknown as BytePair,
  );
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

// A share link's JSON never comes near this; the cap stops a crafted link from
// inflating into hundreds of megabytes (deflate's ratio tops out near 1000:1).
const MAX_INFLATED_BYTES = 1 << 20;

// `complete` is false when the stream stopped short of its final block — a
// link cut in transit (deflate notices this itself, so no checksum is needed).
// What inflated before the cut is still returned.
async function inflateRaw(bytes: Uint8Array<ArrayBuffer>): Promise<{ bytes: Uint8Array; complete: boolean }> {
  const stream = new Blob([bytes]).stream().pipeThrough(
    new DecompressionStream('deflate-raw') as unknown as BytePair,
  );
  const reader = stream.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  let complete = true;
  for (;;) {
    let step: ReadableStreamReadResult<Uint8Array>;
    try {
      step = await reader.read();
    } catch {
      complete = false;
      break;
    }
    const { done, value } = step;
    if (done) break;
    total += value.length;
    if (total > MAX_INFLATED_BYTES) {
      void reader.cancel();
      throw new Error('Share payload too large');
    }
    chunks.push(value);
  }
  const out = new Uint8Array(total);
  let at = 0;
  for (const c of chunks) {
    out.set(c, at);
    at += c.length;
  }
  return { bytes: out, complete };
}

function isWholeJson(text: string): boolean {
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
}

// Key added to every object closeTruncatedJson had to close: its decoder knows
// that object may be missing fields that were past the cut.
export const CUT_MARK = '~cut';

/**
 * Close JSON text that was cut off: keep everything up to the last complete
 * value (never a half string, nor a number that may have lost digits — a value
 * counts as complete only once a `,` or closing bracket follows it), then close
 * the open containers, adding `"~cut":1` to each object closed this way.
 * Returns null when no complete value arrived. Complete JSON comes back as is.
 */
export function closeTruncatedJson(text: string): string | null {
  type Frame = { obj: boolean; members: boolean };
  const stack: Frame[] = [];
  let cutAt = -1;
  const closeAll = (): string => {
    let out = '';
    for (let i = stack.length - 1; i >= 0; i--) {
      const f = stack[i]!;
      out += f.obj ? `${f.members ? ',' : ''}"${CUT_MARK}":1}` : ']';
    }
    return out;
  };
  let inString = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inString) {
      if (ch === '\\') i++;
      else if (ch === '"') inString = false;
      continue;
    }
    if (ch === '"') {
      inString = true;
    } else if (ch === '{' || ch === '[') {
      // The container is a value of its parent, closed by the repair if not by the text.
      if (stack.length > 0) stack[stack.length - 1]!.members = true;
      stack.push({ obj: ch === '{', members: false });
      cutAt = i + 1;
    } else if (ch === ',') {
      if (stack.length === 0) return null;
      stack[stack.length - 1]!.members = true;
      cutAt = i;
    } else if (ch === '}' || ch === ']') {
      stack.pop();
      if (stack.length === 0) return text.slice(0, i + 1);
      stack[stack.length - 1]!.members = true;
      cutAt = i + 1;
    }
  }
  // Every change to the stack is a delimiter that also moves the cut, so the
  // stack left at the end is the one at the cut: close it once, here.
  return cutAt < 0 ? null : text.slice(0, cutAt) + closeAll();
}

// Long links get mangled in transit: mail clients and chat apps wrap them
// (leaving spaces or newlines mid-payload), and auto-linkers swallow trailing
// punctuation (`…xyz).`). None of those characters can occur in base64url, so
// dropping them is lossless.
export function cleanSharePayload(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed.startsWith(SHARE_FORMAT_PREFIX)) return trimmed;
  return SHARE_FORMAT_PREFIX + trimmed.slice(SHARE_FORMAT_PREFIX.length).replace(/[^A-Za-z0-9_-]/g, '');
}

export async function encodeShareState(
  config: AppConfig,
  zoom?: Zoom,
  localLanes?: LocalLaneForShare[],
): Promise<string> {
  // Hidden calendars stay out of a share link (linked and local alike); an
  // export file still carries them, being a full backup.
  const { f, r }: Pick<SharedPayload, 'f' | 'r'> = {
    f: config.feeds
      .filter((f) => f.source.kind === 'user' && !f.hidden)
      .sort((a, b) => a.order - b.order)
      .map((f) => {
        const url = (f.source as { kind: 'user'; url: string }).url;
        return {
          // Assume https for remote feeds: drop the scheme (decode re-adds it).
          // Other schemes travel intact so they still round-trip.
          u: url.startsWith('https://') ? url.slice('https://'.length) : url,
          n: f.name,
          h: f.category === 'holidays' ? 1 : 0,
          ...(f.category && f.category !== 'none' && f.category !== 'holidays' ? { c: f.category } : {}),
          ...(f.timezone ? { tz: f.timezone } : {}),
          ...(f.color ? { cl: f.color } : {}),
          ...(f.block && f.block !== 'none' ? { bl: f.block } : {}),
          ...(f.style && f.style !== 'none' ? { st: f.style } : {}),
          ...(f.subLanes === 'location' ? { sl: 'l' as const } : {}),
        };
      }),
    r: config.rules.map((r) => ({
      i: r.id, f: r.find, s: r.style,
      ...(r.replace !== undefined ? { r: r.replace } : {}),
      ...(r.category && r.category !== 'none' ? { c: r.category } : {}),
      ...(r.color ? { cl: r.color } : {}),
      ...(r.block && r.block !== 'none' ? { bl: r.block } : {}),
      ...(r.position && r.position !== 'any' ? { p: r.position } : {}),
      ...(r.disabled ? { x: 1 as const } : {}),
    })),
  };
  // Local (scratchpad) lanes — the Draft and imported .ics — carry their events
  // inline, since there's no URL to re-fetch. Empty lanes are skipped so a fresh
  // install's always-present empty Draft never bloats every link. When the caller
  // doesn't pass live lanes (e.g. tests, non-UI callers) fall back to localStorage.
  const lanes: LocalLaneForShare[] =
    localLanes ??
    config.feeds
      .filter((f) => f.source.kind === 'scratchpad')
      .map((f) => ({
        feed: f,
        events: loadScratchpad((f.source as { kind: 'scratchpad'; id?: string }).id ?? 'default'),
      }));
  const lf: SharedLocalFeed[] = lanes
    // Keep any visible non-empty lane, plus an empty-but-enabled Draft so its
    // enabled state still travels. Hidden and empty non-Draft lanes are skipped.
    .filter(
      (l) =>
        l.feed.source.kind === 'scratchpad' &&
        !l.feed.hidden &&
        (l.events.length > 0 || l.feed.id === SCRATCHPAD_FEED_ID),
    )
    .sort((a, b) => a.feed.order - b.feed.order)
    .map((l) => ({
      n: l.feed.name,
      ...(l.feed.category && l.feed.category !== 'none' ? { c: l.feed.category } : {}),
      ...(l.feed.timezone ? { tz: l.feed.timezone } : {}),
      ...(l.feed.id === SCRATCHPAD_FEED_ID ? { df: 1 as const } : {}),
      ...(l.feed.subLanes === 'location' ? { sl: 'l' as const } : {}),
      // Last, so a link cut short keeps the lane's settings with the events
      // that arrived (decodeShareState).
      ev: (() => {
        // Delta/minute encode within the sorted lane: first start absolute (in
        // minutes), the rest as deltas from the previous start; e is a duration.
        let prevStartMin = 0;
        return l.events.map((ev, idx) => {
          const startMin = Math.round(ev.start.getTime() / 60000);
          const endMin = Math.round(ev.end.getTime() / 60000);
          const s = idx === 0 ? startMin : startMin - prevStartMin;
          prevStartMin = startMin;
          return {
            t: ev.title,
            s,
            e: endMin - startMin,
            a: ev.allDay ? 1 : 0,
            ...(ev.description ? { d: ev.description } : {}),
            ...(ev.location ? { l: ev.location } : {}),
            ...(ev.url ? { w: ev.url } : {}),
            ...(ev.category && ev.category !== 'none' ? { c: ev.category } : {}),
            ...(ev.rrule ? { r: ev.rrule } : {}),
            ...(ev.rrule && ev.tzid ? { z: ev.tzid } : {}),
            ...(ev.rrule && ev.exdates?.length
              ? { x: ev.exdates.map((d) => Math.round(d.getTime() / 60000) - startMin) }
              : {}),
          };
        });
      })(),
    }));
  const view: SharedView = {};
  if (zoom) view.z = zoom;
  if (config.locale) view.l = config.locale;
  if (config.dateFormat) view.d = config.dateFormat;
  // Shake is device-local — see isShareableMode. The recipient keeps their own.
  if (config.scheme && isShareableMode(config.scheme)) view.t = config.scheme;
  if (config.palette && isShareableMode(config.palette)) view.p = config.palette;
  // The kiosk PIN (its hash, never the PIN) and view first, lanes last: a
  // link cut short keeps the PIN and view whatever the calendars and rules
  // ahead of the lanes weigh, and the lanes (the bulk of a long link) keep the
  // events that arrived.
  const payload: SharedPayload = {
    ...(isPinHash(config.kioskPin) ? { kh: config.kioskPin } : {}),
    ...(Object.keys(view).length > 0 ? { v: view } : {}),
    f,
    r,
    ...(lf.length > 0 ? { lf } : {}),
  };
  const json = JSON.stringify(payload);
  const bytes = new TextEncoder().encode(json);
  return SHARE_FORMAT_PREFIX + toBase64Url(await deflateRaw(bytes));
}

export async function decodeShareState(
  payload: string,
): Promise<DecodedShare | null> {
  if (!payload || typeof payload !== 'string') return null;
  payload = cleanSharePayload(payload);
  if (!payload.startsWith(SHARE_FORMAT_PREFIX)) return null;
  try {
    const bytes = fromBase64Url(payload.slice(SHARE_FORMAT_PREFIX.length));
    const inflated = await inflateRaw(bytes);
    let json: string | null = new TextDecoder().decode(inflated.bytes);
    // A link cut short: keep what arrived. Objects the cut went through carry
    // CUT_MARK and are dropped below (a feed, rule or event may be missing
    // fields), except a local lane, which keeps the events that made it.
    // Deflate also errors on bytes glued on after its end, having inflated
    // everything: then the JSON is whole and nothing was cut.
    let truncated = false;
    if (!inflated.complete && !isWholeJson(json)) {
      truncated = true;
      json = closeTruncatedJson(json);
    }
    if (json == null) return null;
    const parsed = JSON.parse(json) as Partial<SharedPayload>;
    if (!parsed || typeof parsed !== 'object') return null;
    const cut = (o: object): boolean => CUT_MARK in o;
    const rawFeeds = Array.isArray(parsed.f) ? parsed.f : [];
    const rawRules = Array.isArray(parsed.r) ? parsed.r : [];
    const feeds: CalendarFeed[] = [];
    rawFeeds.forEach((f, i) => {
      if (!f || typeof f !== 'object' || cut(f)) return;
      if (typeof f.u !== 'string' || typeof f.n !== 'string') return;
      // Re-add the assumed https scheme; leave any URL that already carries a
      // scheme (https://, http://, webcal://) untouched so we never double it.
      const url = /^[a-z][a-z0-9+.-]*:\/\//i.test(f.u) ? f.u : 'https://' + f.u;
      const source = { kind: 'user' as const, url };
      let category: FeedCategory =
        typeof f.c === 'string' && (FEED_CATEGORIES as string[]).includes(f.c)
          ? (f.c as FeedCategory)
          : f.h === 1
            ? 'holidays'
            : 'none';
      // Legacy: pre-merge links carried travel as a separate `tr` tag — fold it
      // back into the event type.
      if (f.tr === 'international') category = 'travel-international';
      else if (f.tr === 'local') category = 'travel-local';
      const timezone =
        typeof f.tz === 'string' && f.tz.trim().length > 0 ? f.tz.trim() : undefined;
      const color: CalendarColor | undefined =
        typeof f.cl === 'string' && (CALENDAR_COLORS as string[]).includes(f.cl)
          ? (f.cl as CalendarColor)
          : undefined;
      const block: Block | undefined =
        typeof f.bl === 'string' && (BLOCK_OPTIONS as string[]).includes(f.bl) && f.bl !== 'none'
          ? (f.bl as Block)
          : undefined;
      const style: StyleVariant | undefined =
        typeof f.st === 'string' && STYLE_VARIANTS.includes(f.st as StyleVariant) && f.st !== 'none'
          ? (f.st as StyleVariant)
          : undefined;
      feeds.push({
        id: feedIdFor(source),
        source,
        name: f.n,
        collapsed: false,
        order: i,
        kind: category === 'holidays' ? 'holidays' : 'events',
        category,
        ...(timezone ? { timezone } : {}),
        ...(color ? { color } : {}),
        ...(block ? { block } : {}),
        ...(style ? { style } : {}),
        ...(f.sl === 'l' ? { subLanes: 'location' as const } : {}),
      });
    });
    const rules: FindReplaceRule[] = [];
    rawRules.forEach((r) => {
      if (!r || typeof r !== 'object' || cut(r)) return;
      if (typeof r.i !== 'string' || typeof r.f !== 'string') return;
      const style: StyleVariant = STYLE_VARIANTS.includes(r.s) ? r.s : 'none';
      // r absent = matte (match + decorate only); '' (replace with blank) and real
      // replacements are kept.
      const replace: string | undefined =
        typeof r.r === 'string' ? r.r : undefined;
      const category: FeedCategory =
        typeof r.c === 'string' && (FEED_CATEGORIES as string[]).includes(r.c)
          ? (r.c as FeedCategory)
          : 'none';
      const color: CalendarColor | undefined =
        typeof r.cl === 'string' && (CALENDAR_COLORS as string[]).includes(r.cl)
          ? (r.cl as CalendarColor)
          : undefined;
      const block: Block | undefined =
        typeof r.bl === 'string' && (BLOCK_OPTIONS as string[]).includes(r.bl) && r.bl !== 'none'
          ? (r.bl as Block)
          : undefined;
      const position: MatchPosition | undefined =
        typeof r.p === 'string' && (MATCH_POSITIONS as string[]).includes(r.p) && r.p !== 'any'
          ? (r.p as MatchPosition)
          : undefined;
      rules.push({
        id: r.i,
        find: r.f,
        style,
        category,
        ...(replace !== undefined ? { replace } : {}),
        ...(color ? { color } : {}),
        ...(block ? { block } : {}),
        ...(position ? { position } : {}),
        ...(r.x === 1 ? { disabled: true } : {}),
      });
    });
    const rawLocal = Array.isArray(parsed.lf) ? parsed.lf : [];
    const localFeeds: DecodedLocalFeed[] = [];
    rawLocal.forEach((lfd) => {
      if (!lfd || typeof lfd !== 'object') return;
      if (typeof lfd.n !== 'string' || !Array.isArray(lfd.ev)) return;
      let category: FeedCategory =
        typeof lfd.c === 'string' && (FEED_CATEGORIES as string[]).includes(lfd.c)
          ? (lfd.c as FeedCategory)
          : 'none';
      // Legacy travel tag → event type.
      if (lfd.tr === 'international') category = 'travel-international';
      else if (lfd.tr === 'local') category = 'travel-local';
      const timezone =
        typeof lfd.tz === 'string' && lfd.tz.trim().length > 0 ? lfd.tz.trim() : undefined;
      const events: ParsedEvent[] = [];
      // Running absolute start (minutes) to rebuild each event from its delta.
      let prevStartMin = 0;
      lfd.ev.forEach((ev, idx) => {
        if (!ev || typeof ev !== 'object' || cut(ev)) return;
        if (typeof ev.t !== 'string' || typeof ev.s !== 'number' || typeof ev.e !== 'number') return;
        let evCategory =
          typeof ev.c === 'string' && (FEED_CATEGORIES as string[]).includes(ev.c)
            ? (ev.c as FeedCategory)
            : undefined;
        // Legacy travel tag → event type.
        if (ev.tr === 'international') evCategory = 'travel-international';
        else if (ev.tr === 'local') evCategory = 'travel-local';
        // Coarse minute/delta decode: first event's s is absolute minutes, the rest
        // are deltas from the previous start; e is a duration in minutes.
        const startMin = idx === 0 ? ev.s : prevStartMin + ev.s;
        prevStartMin = startMin;
        const start = new Date(startMin * 60000);
        const end = new Date((startMin + ev.e) * 60000);
        const rrule = typeof ev.r === 'string' && parseRRule(ev.r) ? ev.r : undefined;
        const built = makeScratchpadEvent({
          title: ev.t,
          start,
          end,
          allDay: ev.a === 1,
          ...(typeof ev.l === 'string' ? { location: ev.l } : {}),
          ...(typeof ev.d === 'string' ? { description: ev.d } : {}),
          ...(evCategory ? { category: evCategory } : {}),
          ...(rrule ? { rrule } : {}),
          ...(rrule && isValidTimezone(ev.z) ? { tzid: ev.z } : {}),
        });
        if (rrule && Array.isArray(ev.x)) {
          const ex = ev.x.filter((n): n is number => typeof n === 'number' && Number.isFinite(n));
          if (ex.length) built.exdates = ex.map((n) => new Date((startMin + n) * 60000));
        }
        // makeScratchpadEvent has no url field; restore it so links round-trip —
        // but only if it carries a safe scheme, so a crafted link can't smuggle a
        // `javascript:`/`data:` URL into a stored event's "Open source" href.
        if (typeof ev.w === 'string') {
          const safe = safeHref(ev.w);
          if (safe) built.url = safe;
        }
        events.push(built);
      });
      // A lane the cut reached before any of its events: nothing to keep.
      if (cut(lfd) && events.length === 0) return;
      localFeeds.push({
        name: lfd.n,
        category,
        ...(timezone ? { timezone } : {}),
        ...(lfd.h === 1 ? { hidden: true } : {}),
        ...(lfd.df === 1 ? { isDraft: true } : {}),
        ...(lfd.sl === 'l' ? { subLanes: 'location' as const } : {}),
        events,
      });
    });
    let view: SharedView_t | null = null;
    if (parsed.v && typeof parsed.v === 'object') {
      const v: SharedView_t = {};
      const raw = parsed.v;
      if (raw.z && ZOOMS.includes(raw.z)) v.zoom = raw.z;
      if (raw.l && LOCALES.includes(raw.l)) v.locale = raw.l;
      if (raw.d && DATE_FORMATS.includes(raw.d)) v.dateFormat = raw.d;
      // Guarded on decode too, so links shared before the encode side existed
      // can't push Shake onto whoever opens them.
      if (raw.t && SCHEMES.includes(raw.t) && isShareableMode(raw.t)) v.scheme = raw.t;
      if (raw.p && PALETTES.includes(raw.p) && isShareableMode(raw.p)) v.palette = raw.p;
      if (Object.keys(v).length > 0) view = v;
    }
    // Nothing usable arrived: report the link as broken rather than offer an
    // empty import.
    if (truncated && feeds.length === 0 && rules.length === 0 && localFeeds.every((l) => l.events.length === 0)) {
      return null;
    }
    const kioskPin = isPinHash(parsed.kh) ? parsed.kh : null;
    return { feeds, rules, localFeeds, view, kioskPin, truncated };
  } catch {
    return null;
  }
}

// The one decision on what an opened share link does on this device; the
// share-import module feeds it the device's state and acts on the answer.
// - refuse 'insecure': a kiosk link on a page without Web Crypto (plain http),
//   which could neither check the PIN nor drop the lock — never imported
//   unlocked.
// - On a display with no pointer (signage) nobody can answer the import dialog
//   (it would cover the screen for good), so every whole link imports itself,
//   replacing the setup — kiosk links included, even over an existing lock:
//   getting a link opened there already takes control of the device. A link
//   cut short is refused ('cut'): partial calendars must not quietly replace a
//   sign's setup, and nobody could read the dialog's warning.
// - refuse 'locked': a locked kiosk someone could unlock takes no links — a
//   link must not swap what it shows or its PIN.
// - Otherwise only a fresh install imports by itself, and never a kiosk lock
//   or a cut link, which must be shown and accepted: 'ask' opens the dialog.
export function decideShareImport(
  link: { kiosk: boolean; truncated: boolean },
  device: { locked: boolean; noPointer: boolean; fresh: boolean; canCheckPins: boolean },
): ShareImportDecision {
  if (link.kiosk && !device.canCheckPins) return { action: 'refuse', reason: 'insecure' };
  if (device.noPointer) {
    return link.truncated ? { action: 'refuse', reason: 'cut' } : { action: 'auto', sign: true };
  }
  if (device.locked) return { action: 'refuse', reason: 'locked' };
  return device.fresh && !link.kiosk && !link.truncated ? { action: 'auto', sign: false } : { action: 'ask' };
}

// `marker`: the temp marker's fragment value (`markerValue`), passed in rather
// than read from the address so a link built ahead of the tap (in an effect)
// tracks it and never carries a stale date.
export async function buildShareUrl(
  config: AppConfig,
  zoom?: Zoom,
  base?: string,
  localLanes?: LocalLaneForShare[],
  marker?: string | null,
): Promise<string> {
  const payload = await encodeShareState(config, zoom, localLanes);
  const root = base ?? (typeof location !== 'undefined' ? location.origin + location.pathname : '');
  // The payload rides in the fragment, which browsers never send to a server:
  // opening the link reveals nothing to ours, nor to a link-preview fetcher.
  // It carries the temp marker too, so the link restores the viewed date even
  // if the recipient never imports the config — but not the sharer's view
  // settings, which would override the recipient's own.
  return root + '#' + SHARE_PARAM + '=' + payload + (marker ? '&d=' + marker : '');
}

// The fragment first; `?s=` is the pre-fragment form, still honoured (such a
// link has already reached the server by the time it is read).
export function readShareParam(
  hash: string = typeof location !== 'undefined' ? location.hash : '',
  search: string = typeof location !== 'undefined' ? location.search : '',
): string | null {
  return fragmentParams(hash).get(SHARE_PARAM) ?? new URLSearchParams(search).get(SHARE_PARAM);
}

// Native Web Share, with a guard against a well-known browser bug: on iOS Safari
// and Firefox for Android the first navigator.share() promise never settles (the
// browser never reports the share sheet closing), so its internal "share in
// progress" flag stays set and every later call throws InvalidStateError ("an
// earlier share has not yet completed") until the page is reloaded. JS cannot
// reset that flag. We track our own in-flight guard so a repeat tap doesn't hang;
// well-behaved browsers settle the promise and keep sharing natively, while stuck
// browsers report 'stuck' so the caller can copy + hint that a refresh is needed.
let sharePending = false;

export async function tryNativeShare(url: string): Promise<NativeShareResult> {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') {
    return 'fallback';
  }
  // A prior share is still in flight (the stuck state) — don't fire another
  // navigator.share (it would throw); let the caller copy + hint instead.
  if (sharePending) return 'stuck';
  sharePending = true;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const clear = (): void => {
    sharePending = false;
    if (timer) clearTimeout(timer);
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisible);
    }
  };
  // The promise may never settle on the buggy browsers; clear the guard when the
  // user returns to the page (sheet dismissed) or after a timeout so later taps
  // can re-attempt native rather than being blocked on our side forever.
  const onVisible = (): void => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') clear();
  };
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisible);
  }
  timer = setTimeout(clear, 120000);
  try {
    await navigator.share({ url });
    clear();
    return 'shared';
  } catch (err) {
    clear();
    // The user cancelling the share sheet rejects with AbortError. Report that as
    // 'dismissed' so callers skip the clipboard fallback — attempting writeText()
    // before focus returns to the document throws "Document is not focused".
    const name = (err as Error).name;
    if (name === 'InvalidStateError') return 'stuck';
    if (name === 'AbortError') return 'dismissed';
    return 'fallback';
  }
}

export function stripShareParam(): void {
  updateLocation({ [SHARE_PARAM]: null }, [SHARE_PARAM]);
}
